import React, { useId } from 'react';
import { useIntl } from 'react-intl';
import type { CoverRedoKind, CoverRedoPost } from 'fym-shared/src/music24/coverRedo';
import { TypedMemo } from '../../../../../common/types';
import { platformMeta } from '../../../platformMeta';
import type { SocialPlatform } from '../../../types';
import { KIND_GROUP } from './coverRedoKindText';
import { coverRedoMessages as messages } from './coverRedoMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover redo; each part uses its own slice of it */
import styles from './CoverRedo.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The post lines of the dialog: at most 5, then "and N more" (b1). */
const POST_LINES = 5;

const STATUS = {
  draft: messages.postStatusDraft,
  scheduled: messages.postStatusScheduled,
  awaitingManualPost: messages.postStatusNeedsYou,
} as const;

const PostLine: React.FC<{ post: CoverRedoPost }> = ({ post }) => {
  const intl = useIntl();
  const meta = platformMeta[post.platform as SocialPlatform] as (typeof platformMeta)[SocialPlatform] | undefined;
  return (
    <div className={styles.post}>
      <span>
        <b>{intl.formatMessage(KIND_GROUP[post.kind])}</b>
        <small>
          {meta?.name ?? post.platform} · {intl.formatMessage(STATUS[post.status])}
        </small>
      </span>
      <span className={styles.when}>
        {intl.formatDate(post.scheduledAt, { weekday: 'short', day: 'numeric', month: 'short' })}
        {', '}
        {intl.formatTime(post.scheduledAt, { hour: '2-digit', minute: '2-digit' })}
      </span>
    </div>
  );
};

/**
 * What the post part of a redo dialog reads. The cover redo dialog model has it; the date redo
 * dialog gives every group as ticked.
 */
export interface RedoPostsModel {
  state: { scheduledPosts: CoverRedoPost[]; publishedPostCount: number } | undefined;
  ticked: readonly CoverRedoKind[];
  swapPosts: boolean;
  toggleSwap: () => void;
}

/**
 * The posts of the dialog (b1): the posts that did not go out and use an old file, the switch
 * "Move them to the new files" (on by default) and the note about the posts that went out. It hides
 * when no open post uses an old file.
 */
const CoverRedoPostsComponent: React.FC<{ model: RedoPostsModel }> = ({ model }) => {
  const intl = useIntl();
  const switchId = useId();
  const { state } = model;
  if (state === undefined || state.scheduledPosts.length === 0) {
    return null;
  }
  const posts = state.scheduledPosts.filter((post) => model.ticked.includes(post.kind));
  const more = posts.length - POST_LINES;
  return (
    <div className={styles.section} data-testid="cover-redo-posts">
      <div className={styles.eyebrow}>
        <span>{intl.formatMessage(messages.postsTitle, { count: posts.length })}</span>
      </div>
      {posts.slice(0, POST_LINES).map((post) => (
        <PostLine key={post.postUuid} post={post} />
      ))}
      {more > 0 && <p className={styles.note}>{intl.formatMessage(messages.postsMore, { count: more })}</p>}
      <label htmlFor={switchId} className={styles.switchRow}>
        <span className={styles.switchText}>
          <b>{intl.formatMessage(messages.postsMove)}</b>
          <small>{intl.formatMessage(messages.postsMoveNote)}</small>
        </span>
        <input
          type="checkbox"
          id={switchId}
          role="switch"
          className={styles.check}
          checked={model.swapPosts}
          onChange={model.toggleSwap}
          data-testid="cover-redo-swap"
        />
      </label>
      <p className={styles.note}>{intl.formatMessage(messages.postsPublished, { count: state.publishedPostCount })}</p>
    </div>
  );
};

export const CoverRedoPosts = TypedMemo(CoverRedoPostsComponent);
